/**
 * Kitchen Assembly Constants
 * Shared between cloud functions and web app.
 */

const INCHES_TO_METERS = 0.0254;

/** Standard lower-cabinet dimensions (without countertop). */
const KITCHEN_CABINET_DIMENSIONS = {
  height: 34.5 * INCHES_TO_METERS, // 0.8763 m
  depth: 24 * INCHES_TO_METERS, // 0.6096 m
};

/**
 * The recess a base cabinet stands on. Anything built INTO a base cabinet —
 * a dishwasher, an under-counter oven — starts here, not on the floor.
 */
const KITCHEN_TOE_KICK_HEIGHT = 4 * INCHES_TO_METERS; // 0.1016 m

const KITCHEN_COUNTERTOP_THICKNESS = 1.5 * INCHES_TO_METERS; // 0.0381 m

/** Front overhang past the cabinet face — standard 1" reveal. */
const KITCHEN_COUNTERTOP_FRONT_OVERHANG = 1 * INCHES_TO_METERS; // 0.0254 m

/**
 * Knee overhang past the cabinet face on a SEATED edge (island or
 * peninsula). NKBA's minimum for a 36"-high counter: 12" of countertop
 * cantilever so a seated diner's knees clear the cabinet body. Replaces
 * KITCHEN_COUNTERTOP_FRONT_OVERHANG on the one edge that carries stools.
 *
 * Shared by BOTH materializers — the auto kitchen (`barstoolSeating.js`
 * and the island builders) and the manual cabinet-run engine
 * (`cabinet-runs/freestanding.js`, which extends a free-standing run's
 * countertop outward by it). They apply it on different terms, and that
 * difference is deliberate to the extent it is understood:
 *   - auto seats an edge only when it clears `KITCHEN_SEATING_CLEARANCE`,
 *     keeps its seating strip out of the walkways, and holds at least
 *     `BARSTOOL_MIN_SEATS` — and only then emits stools;
 *   - manual applies the overhang UNCONDITIONALLY to every free-standing
 *     run's outward side, and emits no stools at all.
 * So a hand-drawn island can carry a knee overhang nobody can sit at.
 * Reconciling that is open work; keep the dimension itself in one place
 * regardless, or the two engines drift on the number as well as the rule.
 */
const KITCHEN_SEATING_OVERHANG = 12 * INCHES_TO_METERS; // 0.3048 m

/**
 * Free depth required BEYOND the seated edge's counter lip, measured to
 * the nearest wall / cabinet / obstruction. NKBA's minimum for a seated
 * diner, and simultaneously this codebase's walkway floor
 * (ISLAND_AISLE_MIN / KITCHEN_MIN_WALKWAY) — so an edge that clears it
 * cannot have pinched circulation below what the kitchen already
 * required before the overhang existed. Walkway corridors are gated
 * separately, against the narrower SEATING_STRIP_DEPTH (what the
 * counter and stools physically occupy); see `barstoolSeating.js` for
 * why testing this zone against them instead seated nobody.
 */
const KITCHEN_SEATING_CLEARANCE = 36 * INCHES_TO_METERS; // 0.9144 m

/**
 * Counter of seated width per stool. NKBA allots 24" of counter edge per
 * counter-height seat, which is wider than the stool itself — the extra
 * is elbow room.
 */
const BARSTOOL_SEAT_SPACING = 24 * INCHES_TO_METERS; // 0.6096 m

/**
 * Fewest seats worth building a seated edge for. A lone stool at a
 * counter reads as an afterthought rather than a seating plan, and it
 * costs the same 12" of aisle as a real one — so an edge that can only
 * take one seat keeps its plain 1" reveal and gets no stools at all.
 */
const BARSTOOL_MIN_SEATS = 2;

/**
 * Barstool footprint + seat height. Seat height is set for a 36" counter
 * (NKBA pairs a 24"–26" seat with a 36" counter); the `bar-stool`
 * procedural renderer treats `dimensions.height` as the seat top and
 * grows the backrest ~7" above it, so the stool still tucks under the
 * slab. The 35"-tall entry in the admin procedural gallery is the
 * 42"-bar variant and is deliberately not reused here.
 */
const BARSTOOL_DIMENSIONS = {
  width: 17 * INCHES_TO_METERS, // 0.4318 m
  depth: 17 * INCHES_TO_METERS, // 0.4318 m
  height: 25 * INCHES_TO_METERS, // 0.635 m — seat top
};

/** Buffer kept between a cabinet end and the nearest door / opening edge. */
const OPENING_BUFFER = 4 * INCHES_TO_METERS; // 0.1016 m

/**
 * How far a SCANNED appliance may be slid along its wall to reach a buildable
 * layout. Up to this it is scan noise; past it the measurement is a real
 * discrepancy (a mis-labelled or phantom appliance, a sink that really is
 * there) and the scan is left as it stands. Shared by post-processing's
 * appliance separation (`separateRunAppliances`) and the run engine's
 * sink-to-corner slack (`sinkSlackToEnd`), so "small" means one thing.
 */
const MAX_SCAN_APPLIANCE_SLIDE = 6 * INCHES_TO_METERS; // 0.1524 m

/**
 * Minimum aisle clearance from any face of an island to the nearest
 * cabinet / appliance face. NKBA's hard minimum for a single-cook
 * kitchen — anything tighter and the dishwasher / oven door can't
 * open without hitting the island.
 */
const ISLAND_AISLE_MIN = 36 * INCHES_TO_METERS; // 0.9144 m

/**
 * NKBA's work aisle for ONE cook — the aisle the scorer stops penalising at
 * (`scoring/entranceClearance`), and the island grade `'comfortable'` (≥42",
 * short of the 48" `'preferred'`). 36" is the walkway minimum, 42" a work
 * aisle for one cook, 48" for more than one (user ruling 2026-09-21). A future
 * cook count in the spec would pick 42 or 48 here.
 */
const ISLAND_AISLE_ONE_COOK = 42 * INCHES_TO_METERS; // 1.0668 m

/**
 * Preferred aisle clearance — comfortable two-cook circulation. Used
 * as a quality flag (`aisleClearance: 'preferred'`), not a hard
 * reject. Layouts that hit this on every side feel noticeably nicer.
 */
const ISLAND_AISLE_PREFERRED = 48 * INCHES_TO_METERS; // 1.2192 m

/**
 * Maximum island length we'll ever propose. 10ft is a comfortable
 * upper bound for residential kitchens — beyond this islands tend
 * to need internal divisions (sink + cooktop) we don't yet support.
 */
const ISLAND_MAX_LENGTH = 120 * INCHES_TO_METERS; // 3.048 m

/**
 * Upper bound for island depth (the short-axis dimension). 48" is
 * the NKBA-comfortable depth for two-sided use with full seating
 * overhang. Beyond this islands feel oversized in residential
 * kitchens. Below this we sweep down to KITCHEN_CABINET_DIMENSIONS.depth
 * (24") in steps of ISLAND_DEPTH_STEP, picking the biggest valid fit.
 */
const ISLAND_MAX_DEPTH = 48 * INCHES_TO_METERS; // 1.2192 m

/** Depth-sweep granularity. 6" lets us pick 24", 30", 36", 42", 48". */
const ISLAND_DEPTH_STEP = 6 * INCHES_TO_METERS; // 0.1524 m

/** Wall segments shorter than this are skipped — too narrow for a real cabinet. */
const MIN_CABINET_WIDTH = 12 * INCHES_TO_METERS; // 0.3048 m

/**
 * The narrowest leftover span that becomes a real standalone CABINET rather
 * than being absorbed by its neighbour or capped with a filler panel.
 *
 * 9", which is `MIN_MODULE_IN` — the narrowest box this codebase sells, and
 * where the Semi-Custom grid's width list starts. Post-processing had already
 * reached the same number independently (`oblique-junctions.js`'s
 * `MIN_CABINET_WIDTH_M = 9 * IN`, documented in `run-spans.js` as "the
 * narrowest real cabinet"), so this unifies two notions rather than inventing
 * one.
 *
 * DISTINCT from `MIN_CABINET_WIDTH` (12"), which answers different questions
 * that keep their own threshold: "does this wall have a run at the corner?"
 * (`classifyCorner`'s `aHasRun`), "is this gap a reveal rather than a cabinet?"
 * (`runEndClosure`'s `MAX_REVEAL`), and the corner-run scoring. Only the
 * cabinet-vs-filler decision moves.
 *
 * User ruling 2026-09-11: a 10" gap beside a corner unit should be a 10" box,
 * not a dead panel and not a reason to stretch the corner unit over it.
 */
const MIN_STANDALONE_CABINET_WIDTH = 9 * INCHES_TO_METERS; // 0.2286 m

/**
 * Smallest along-wall extent that becomes a real CABINET. Standard base
 * cabinets exist down to 6" (spice pull-outs, narrow drawer banks); below
 * this a span is a blank-panel filler instead. This is the cabinet-vs-filler
 * line, and it is enforced in `typedBayFill.buildTypedBaysForSpan` — the one
 * place that turns a solved span into objects — so a caller cannot disagree
 * with it by pre-gating at a different width.
 */
const MIN_FILL_CABINET_WIDTH = 6 * INCHES_TO_METERS; // 0.1524 m

/**
 * Tolerance gap shaved off a cabinet / filler edge that abuts an appliance, so
 * the cabinet's side face doesn't share a plane with the appliance's side face
 * (coplanar faces produce visible z-fighting in the 3D scene). Half a
 * millimetre is too thin to read as a gap but comfortably defeats the coplanar
 * render.
 *
 * It lives HERE because two passes have to agree on it: the fill shaves it, and
 * anything asking "are these two bodies one run" has to bridge it. They did not
 * agree — `endpointSnap`'s contiguity test used a 0.1 mm epsilon, so a fill
 * cabinet beside an appliance-hosted cabinet always read as a separate run and
 * the hosted cabinet's INNER edge became a movable run terminus. The endpoint
 * snap then stretched it back over the cabinet already filled there (measured
 * 2026-09-12 on 1CFF46CC L+floating-L: a sink base grown 36" -> 37.8" straight
 * through a 12" drawer base). A run break must be wider than the hairline the
 * fill deliberately leaves.
 */
const APPLIANCE_GAP = 0.0005; // 0.5 mm

/** Total length along each wall claimed by a corner cabinet (24" corner box + 12" extension). */
const CORNER_CABINET_FOOTPRINT = 36 * INCHES_TO_METERS; // 0.9144 m

/**
 * Length along the wall of a single-sided corner cabinet — used when one
 * wall has a sink (or other non-cabinet appliance) close to the corner
 * but the perpendicular wall has a row of cabinets reaching it. The
 * single-sided cabinet sits at the corner end of the perpendicular
 * wall; the sink wall covers the remaining gap to the corner with a
 * blank panel. Same 36" length as the L-shape variant, but only on one
 * wall.
 */
const SINGLE_SIDED_CORNER_LENGTH = 36 * INCHES_TO_METERS; // 0.9144 m
// The filler between an inside corner's two door faces. At a blind or blank
// corner the trimmed leg's first door would otherwise start exactly where the
// perpendicular unit's door face ends, handle against handle; cabinet shops
// leave a 3" scribe there (and often pull the blind unit the same). The
// enumeration reserves it (`TYPOLOGY_CORNER_RESERVE_M`), the pre-pass keeps a
// sliding sink out of it, and the run engine builds it as a filler.
const INSIDE_CORNER_REVEAL = 3 * INCHES_TO_METERS; // 0.0762 m
/**
 * The longest a single-sided (blind) corner unit may grow — the widest blind
 * base made. A cabinet run lets the unit take a remainder too narrow to be a
 * cabinet of its own (`MIN_CABINET_WIDTH`) up to this, so 42" of leg is a 42"
 * unit rather than 36" and a 6" filler.
 */
const MAX_SINGLE_SIDED_CORNER_LENGTH = 48 * INCHES_TO_METERS; // 1.2192 m

/**
 * Narrowest door leaf an L corner unit may show on either leg.
 *
 * `leaf = leg − the corner box`, so this is really a floor on the leg. 9"
 * because that is `MIN_MODULE_IN` — the narrowest box this codebase sells, and
 * the narrowest door with it. A 12" leaf is an ordinary narrow cabinet door and
 * stays; a 5" one is not a product, which is what a 17" upper leg against the
 * 12" upper box produces (4 of 113 emitted uppers, 2026-09-10).
 *
 * The LOWER path needs no such rule — its L is always 36×36 against a ≤24" box,
 * so its leaves cannot fall below 12" in the first place.
 */
const MIN_CORNER_LEAF = 9 * INCHES_TO_METERS; // 0.2286 m

/**
 * Length along each wall of an UPPER corner cabinet's leg: the standard 24"
 * wall corner (user ruling 2026-09-19), not the 36" the corner BASE below it
 * takes. A leg may be shorter — it shrinks to the wall's clear run — but never
 * below the 12" corner box plus `MIN_CORNER_LEAF`; see `upperCornerLegs.js`,
 * the one reader both the generator and the run engine decide an upper L by.
 */
const UPPER_CORNER_FOOTPRINT = 24 * INCHES_TO_METERS; // 0.6096 m

/**
 * The highest an opening's TOP may stand off the floor and still let upper
 * cabinets run ACROSS it (user ruling 2026-09-19). Above this — a door, an
 * ordinary window over the counter — the opening blocks the upper band. At or
 * below it — a low window, a pass-through — the uppers hang over it, raised to
 * clear its head (`cabinet-runs/upperTier`).
 */
const UPPER_OVER_OPENING_MAX_TOP = 60 * INCHES_TO_METERS; // 1.524 m

/** Standard upper-cabinet dimensions (wall-mounted). */
const UPPER_CABINET_DIMENSIONS = {
  height: 30 * INCHES_TO_METERS, // 0.762 m
  depth: 12 * INCHES_TO_METERS, // 0.3048 m
};

/**
 * Depth of a shallow, storage-only lower-cabinet run — placed on a wall
 * where a full 24" run plus its walkway won't fit but a slim 12" run
 * still leaves a usable walkway. Same nominal depth as an upper cabinet,
 * but a distinct concept (a floor-standing base run), so it gets its own
 * constant.
 */
const SHALLOW_CABINET_DEPTH = 12 * INCHES_TO_METERS; // 0.3048 m

/**
 * A shallow run is only worth emitting when going from 24" to 12" depth
 * unlocks at least this much extra along-wall cabinet length (≥ 2 bays).
 * Guards against sprouting a token shallow cabinet for a marginal gain.
 */
const MIN_USEFUL_SHALLOW = 24 * INCHES_TO_METERS; // 0.6096 m

/**
 * NKBA minimum clear walkway that must remain in front of a shallow run,
 * measured from the cabinet face to the nearest facing obstacle. (Same
 * 36" floor as ISLAND_AISLE_MIN and typology's MIN_WALKWAY, but a
 * separate concept — the walkway behind a wall-backed shallow run.)
 */
const KITCHEN_MIN_WALKWAY = 36 * INCHES_TO_METERS; // 0.9144 m

/**
 * HARD minimum clear OPENING a mouth into a work zone must leave — the gap a
 * peninsula's open end leaves to the run or wall it faces. NKBA's 36" walkway
 * minimum, the floor every other walkway is held to. Between it and
 * `KITCHEN_WALKWAY_OPENING_PREFERRED` the peninsula is allowed and its measured
 * opening is GRADED like any walkway (`appendageOpeningIn` →
 * `entranceClearanceIn` → `entranceClearance`, −2/inch below 42"), so a tight
 * mouth loses points rather than vanishing.
 *
 * It was 48" until 2026-10-29, which refused an 87" peninsula leaving 40" to a
 * bare wall in 2425 San Fernando Ct's 127"-wide kitchen — a peninsula whose
 * shorter rungs could not hold a sink and a dishwasher. User ruling: drop the
 * hard minimum to 36" and lose points below 42"/48".
 */
const KITCHEN_WALKWAY_OPENING_MIN = 36 * INCHES_TO_METERS; // 0.9144 m

/**
 * PREFERRED clear opening into a work zone — the full 48" walkway. What the
 * builders that pick ONE length design to: the floating-U and L+floating-L
 * ladders take the largest arm that still leaves this, and the between-counter
 * separations hold to it, so lowering the hard floor adds peninsula options
 * without trading those builders' roomy arms for tight ones.
 */
const KITCHEN_WALKWAY_OPENING_PREFERRED = 48 * INCHES_TO_METERS; // 1.2192 m

/**
 * Single-bowl kitchen sink basin (rectangular undermount). 27" × 17" × 8"
 * fits inside a 36" sink-housing cabinet with room for the disposal flange.
 */
const KITCHEN_SINK_BASIN = {
  width: 27 * INCHES_TO_METERS,
  depth: 17 * INCHES_TO_METERS,
  height: 8 * INCHES_TO_METERS,
};

/**
 * Double-bowl kitchen sink basin (equal bowls, rectangular undermount),
 * picked from the design's generic tiles (2026-10-27): 32" × 18" × 9", the
 * common double that drops into a 36" sink base. Its box is both bowls and
 * the divider between them.
 */
const KITCHEN_DOUBLE_SINK_BASIN = {
  width: 32 * INCHES_TO_METERS,
  depth: 18 * INCHES_TO_METERS,
  height: 9 * INCHES_TO_METERS,
};

/**
 * Along-wall width of the sink-housing base cabinet. 36" is the standard
 * sink base in North American kitchens. It must be ≥ the widest sink we
 * may select: model selection (`planApplianceModels`) caps the sink-GLB
 * query by this cabinet's footprint, and our catalog's real single-bowl
 * sinks are 32–35" — a narrower base would reject all of them and leave
 * only a 15" bar sink. Shared by the island (`buildIslandWithSink`) and
 * wall (`applianceRequirements`) sink placements so both stay in sync.
 */
const KITCHEN_SINK_BASE_WIDTH = 36 * INCHES_TO_METERS; // 0.9144 m

/** Along-wall width of a standard dishwasher (and its DW base slot). */
const DISHWASHER_WIDTH = 24 * INCHES_TO_METERS; // 0.6096 m

/**
 * Kitchen faucet bbox — gooseneck post + spout. Post anchors at the back
 * of the bbox; the 11" depth lets the spout reach forward over the basin
 * center.
 */
const KITCHEN_FAUCET_DIMS = {
  width: 4 * INCHES_TO_METERS,
  height: 14 * INCHES_TO_METERS,
  depth: 11 * INCHES_TO_METERS,
};

/** Vertical gap between countertop top and the bottom of the upper cabinet (standard 18" backsplash). */
const UPPER_CABINET_GAP_ABOVE_COUNTER = 18 * INCHES_TO_METERS; // 0.4572 m

/** Vent hood mounted above the stove. Tall capture chamber so the
 * hood top lands at the same height as the surrounding upper
 * cabinets' tops, giving a more "grand" look than a stub box. The
 * 24" depth pushes the bottom front face out past the lower cabinet
 * front by a couple of inches. */
const VENT_HOOD_DEPTH = 24 * INCHES_TO_METERS; // 0.6096 m
const VENT_HOOD_HEIGHT = 24 * INCHES_TO_METERS; // 0.6096 m
/** Distance from cooktop top to the bottom of the vent hood (standard 24"). */
const VENT_HOOD_GAP_ABOVE_STOVE = 24 * INCHES_TO_METERS; // 0.6096 m

/** Default room height when ceiling data is absent — 8' / 96". */
const DEFAULT_CEILING_HEIGHT = 96 * INCHES_TO_METERS; // 2.4384 m

/**
 * Required clear depth in front of an opening on a wall, measured along
 * that wall's inward normal. A cabinet or appliance on an adjoining
 * wall whose footprint intrudes into this zone is rejected. Doors get a
 * deeper zone than passage openings / windows so a person can walk
 * through the door swing without hitting the cabinet face.
 */
const DOOR_FRONT_CLEARANCE = 54 * INCHES_TO_METERS; // 1.3716 m
const OPENING_FRONT_CLEARANCE = 30 * INCHES_TO_METERS; // 0.762 m
const WINDOW_FRONT_CLEARANCE = 30 * INCHES_TO_METERS; // 0.762 m

/**
 * How much CLEAR PASSAGE a wide cased opening must keep, rather than its
 * whole width.
 *
 * A door reserves its full width because the leaf sweeps it. A door-less
 * opening does not: it is a passage, and what has to stay clear is a way
 * THROUGH, not every inch of it. Reserving the full width made an 84.5"
 * cased opening keep 84.5" of the adjoining wall free, which on E5DED26F
 * rejected all 112 U-shape candidates on the fridge and left galley the
 * only typology the room could produce.
 *
 * 48" rather than a tighter figure so a generous opening does not come out
 * feeling like a corridor (user ruling). Openings NARROWER than this keep
 * the old all-or-nothing rule — there is no passage to preserve inside a
 * 30" opening, so any intrusion still rejects.
 *
 * This is the same shape as the bathroom's clearance slide, where a tub's
 * 24" access zone may sit anywhere within the tub's own footprint:
 * `shared-lib/bathroom-analyzer/pipeline/clearanceSlide.js`.
 */
const OPENING_PASSAGE_CLEARANCE = 48 * INCHES_TO_METERS; // 1.2192 m

/**
 * Required clear standing depth directly in front of a WORK appliance — the
 * sink, the range/cooktop, and the dishwasher (the ones you stand at to work
 * or must open to load) — measured along its wall's inward normal from the
 * appliance's front face. An appliance on a PERPENDICULAR wall jammed near the
 * same inside corner, whose body intrudes into this zone, blocks the approach,
 * so that candidate is rejected. A proper corner cabinet pushes the appliance
 * ≥ one cabinet-depth off the corner, which clears this zone — so only pairs
 * jammed together at an inside corner are caught.
 *
 * 21" is NKBA's STANDING SPACE (its dishwasher guideline), the room to stand at
 * something and open it — which is also what a BASE CABINET needs in front of it
 * in a dead end, so the same value governs `frontClearanceGate.deadEndStretches`:
 * a run stops where a dead-end stretch would leave a cabinet less than this. Not
 * an aisle or walkway width (that is `KITCHEN_MIN_WALKWAY`).
 */
const APPLIANCE_FRONT_CLEARANCE = 21 * INCHES_TO_METERS; // 0.5334 m

/**
 * FRIDGE stand-off from a two-run inside corner (an L/U corner cabinet or a
 * peninsula attachment): the fridge must CLEAR THE CORNER UNIT — its 36" leg,
 * `CORNER_CABINET_FOOTPRINT` — so it never stands in the corner or hard against
 * the other run. A HARD rule, enforced at enumeration.
 *
 * It was 48" (51" before that): the corner unit plus a 12" landing. The landing
 * half was never needed — the corner unit's own counter IS landing, and landing
 * is measured that way (`landingZones.sameWallLanding` runs to the corner) and
 * judged soft by `fridgeLanding`. Held at 48", a fridge beside a corner unit and
 * a door — 2425 San Fernando Ct's, as built — had nowhere to stand (user ruling
 * 2026-10-29: clearing the corner unit is sufficient). Standing inside 48" now
 * costs a little score instead (`FRIDGE_INSIDE_CORNER_PREFERRED`).
 */
const FRIDGE_INSIDE_CORNER_BUFFER = CORNER_CABINET_FOOTPRINT; // 36" = 0.9144 m

/**
 * Grace on `FRIDGE_INSIDE_CORNER_BUFFER`: a fridge may stand up to ½" short of
 * the corner unit's 36" line. The fridge's seats are discrete — flush against a
 * door's 4" `OPENING_BUFFER`, a corner reservation, a slot — and where the wall
 * between them is a hair short, the seat lands a hair inside the line. 2425 San
 * Fernando Ct's fridge beside its door lands 35.98" off the corner: the opening
 * is 69.98" away, so the corner unit and the fridge (66") leave 3.98" beside the
 * door where the planner keeps 4" — a margin, not a wall (user ruling
 * 2026-10-29: allow it unless it's physically impossible).
 */
const FRIDGE_CORNER_FIT_TOL = 0.5 * INCHES_TO_METERS; // 0.0127 m

/**
 * Where a fridge PREFERS to stand from a two-run inside corner: the corner unit
 * plus a 12" landing beside it, the old hard stand-off. Inside it the layout is
 * allowed but scores lower (`scoring/fridgeTightCorner`), so the kitchens that
 * already met it keep their ranking.
 */
const FRIDGE_INSIDE_CORNER_PREFERRED = 48 * INCHES_TO_METERS; // 1.2192 m

/**
 * RANGE/cooktop stand-off from a two-run inside corner. A cooking surface flush
 * against the perpendicular wall/corner cabinet is unsafe (burner heat on the
 * wall, pan handles with no clearance) and leaves no staging landing on that
 * side. 15" keeps the burners off the corner with room for a side landing,
 * while staying small enough not to ban the range from a short flank.
 */
const RANGE_INSIDE_CORNER_BUFFER = 15 * INCHES_TO_METERS; // 0.381 m

/** Total along-wall width of the fridge surround box (slightly wider than the fridge itself for clearance). */
const FRIDGE_SURROUND_WIDTH = 31 * INCHES_TO_METERS; // 0.7874 m

/** Thickness of each vertical side panel that flanks the fridge (1/2"). */
const FRIDGE_SIDE_PANEL_THICKNESS = 0.5 * INCHES_TO_METERS; // 0.0127 m

/**
 * The least filler between a fridge column and a SIDE WALL (user ruling
 * 2026-09-20). A fridge door has to swing past the wall to open to 90° and let
 * the drawers out, and a column hard against one cannot. 3" is the cabinet
 * trade's answer — it is also the narrower of the two stock filler widths (3"
 * and 6"), so it is the piece an installer actually has. Appliance makers ask
 * for anything from 2" (a door swinging against a wall) to 3 3/4" (Samsung,
 * "to open 90° without the handle contacting the wall"); the filler is a floor,
 * not a substitute for the chosen model's install sheet.
 */
const FRIDGE_WALL_FILLER_MIN = 3 * INCHES_TO_METERS; // 0.0762 m

/** Vertical gap between the fridge top and the bottom of the over-fridge cabinet. */
const FRIDGE_GAP_ABOVE = 1 * INCHES_TO_METERS; // 0.0254 m

/**
 * How much a cabinet / appliance is allowed to intrude on a walkway
 * corridor before the corridor blocks the placement. Tight kitchens
 * tend to have walkway envelopes that nip the cabinet zone by an inch
 * or two — accepting that small overlap unlocks layouts in those rooms
 * without producing visibly intrusive cabinets in roomier kitchens.
 */
const CORRIDOR_OVERLAP_TOLERANCE = 2 * INCHES_TO_METERS; // 0.0508 m

/**
 * Max distance a cabinet run's terminal edge is nudged to line up with a
 * nearby alignment anchor — the edge of an opening on the opposing (facing)
 * wall, the island's end edge, or the opposing wall's own run endpoint (the
 * two arms of a U-kitchen). Purely cosmetic: only fires when the run end is
 * already within this of an anchor, so it removes *slight* misalignment
 * without ever forcing alignment or opening a real gap. Tweakable.
 */
const RUN_ENDPOINT_SNAP_TOLERANCE = 2 * INCHES_TO_METERS; // 0.0508 m

module.exports = {
  KITCHEN_TOE_KICK_HEIGHT,
  INCHES_TO_METERS,
  KITCHEN_CABINET_DIMENSIONS,
  KITCHEN_COUNTERTOP_THICKNESS,
  KITCHEN_COUNTERTOP_FRONT_OVERHANG,
  KITCHEN_SEATING_OVERHANG,
  KITCHEN_SEATING_CLEARANCE,
  BARSTOOL_SEAT_SPACING,
  BARSTOOL_MIN_SEATS,
  BARSTOOL_DIMENSIONS,
  OPENING_BUFFER,
  MAX_SCAN_APPLIANCE_SLIDE,
  ISLAND_AISLE_MIN,
  ISLAND_AISLE_ONE_COOK,
  ISLAND_AISLE_PREFERRED,
  ISLAND_MAX_LENGTH,
  ISLAND_MAX_DEPTH,
  ISLAND_DEPTH_STEP,
  MIN_CABINET_WIDTH,
  MIN_STANDALONE_CABINET_WIDTH,
  MIN_FILL_CABINET_WIDTH,
  APPLIANCE_GAP,
  CORNER_CABINET_FOOTPRINT,
  SINGLE_SIDED_CORNER_LENGTH,
  INSIDE_CORNER_REVEAL,
  MAX_SINGLE_SIDED_CORNER_LENGTH,
  MIN_CORNER_LEAF,
  UPPER_CORNER_FOOTPRINT,
  UPPER_OVER_OPENING_MAX_TOP,
  UPPER_CABINET_DIMENSIONS,
  SHALLOW_CABINET_DEPTH,
  MIN_USEFUL_SHALLOW,
  KITCHEN_MIN_WALKWAY,
  KITCHEN_WALKWAY_OPENING_MIN,
  KITCHEN_WALKWAY_OPENING_PREFERRED,
  UPPER_CABINET_GAP_ABOVE_COUNTER,
  KITCHEN_SINK_BASIN,
  KITCHEN_DOUBLE_SINK_BASIN,
  KITCHEN_SINK_BASE_WIDTH,
  DISHWASHER_WIDTH,
  KITCHEN_FAUCET_DIMS,
  VENT_HOOD_DEPTH,
  VENT_HOOD_HEIGHT,
  VENT_HOOD_GAP_ABOVE_STOVE,
  DEFAULT_CEILING_HEIGHT,
  FRIDGE_SURROUND_WIDTH,
  FRIDGE_SIDE_PANEL_THICKNESS,
  FRIDGE_WALL_FILLER_MIN,
  FRIDGE_GAP_ABOVE,
  CORRIDOR_OVERLAP_TOLERANCE,
  RUN_ENDPOINT_SNAP_TOLERANCE,
  DOOR_FRONT_CLEARANCE,
  OPENING_FRONT_CLEARANCE,
  WINDOW_FRONT_CLEARANCE,
  OPENING_PASSAGE_CLEARANCE,
  APPLIANCE_FRONT_CLEARANCE,
  FRIDGE_INSIDE_CORNER_BUFFER,
  FRIDGE_CORNER_FIT_TOL,
  FRIDGE_INSIDE_CORNER_PREFERRED,
  RANGE_INSIDE_CORNER_BUFFER,
};
